// eslint-disable-next-line no-restricted-imports
import { LoaderCircle } from 'lucide-react';
import React from 'react';

// To add a new icon, add a named re-export to IconRegistry.ts.
import * as IconRegistry from './IconRegistry';
import { VariantProps, cn, tv } from './utils/cn';

const styles = tv({
  base: 'shrink-0',
  variants: {
    size: {
      auto: '',
      xs: 'size-3',
      sm: 'size-3.5',
      md: 'size-4',
      lg: 'size-5',
      xl: 'size-6',
      '2xl': 'size-7',
      '3xl': 'size-8',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

/**
 * Union type of all available icon names in the registry.
 *
 * @example
 * const name: IconName = 'Check';
 */
export type IconName = keyof typeof IconRegistry;

/**
 * Array of all available icon names. Useful for programmatic enumeration.
 */
export const iconNames = Object.keys(IconRegistry) as IconName[];

/**
 * Runtime type guard that checks whether a string is a valid icon name.
 * Use this when icon names come from external data (e.g. server-driven UI).
 *
 * @example
 * if (isValidIconName(nameFromApi)) {
 *   return <Icon name={nameFromApi} />;
 * }
 */
export const isValidIconName = (name: string): name is IconName =>
  name in IconRegistry;

/**
 * Props for the {@link Icon} component.
 *
 * Extends standard SVG element props so you can pass `data-*`, `aria-*`,
 * event handlers, etc. directly through to the underlying `<svg>`.
 */
export type IconProps = VariantProps<typeof styles> &
  Omit<React.SVGProps<SVGSVGElement>, 'name' | 'ref'> & {
    name: IconName;
  };

/**
 * Renders a Lucide icon by name with standardized sizing.
 *
 * All icons are sourced from the centralized {@link IconRegistry}. Direct
 * imports from `lucide-react` are blocked by an ESLint rule, add new icons
 * to `IconRegistry.ts` instead. Lucide automatically sets `aria-hidden="true"`
 * when no accessibility props (e.g. `aria-label`) are provided.
 *
 * @example
 * <Icon name="Check" size="sm" />
 * <Icon name="AlertCircle" size="xs" className="text-text-negative" />
 * <Icon name="ChevronDown" aria-label="Expand" />
 */
export const Icon = React.forwardRef<SVGSVGElement, IconProps>(
  ({ name, size, className, ...rest }, ref) => {
    const Component = IconRegistry[name];
    const classes = styles({ size, className });

    return <Component ref={ref} className={classes} {...rest} />;
  },
);

/**
 * Renders an animated loading spinner icon.
 *
 * Use this instead of `<Icon name="..." className="animate-spin" />` for any
 * generic loading state. The animation is built in, do not add `animate-spin`
 * to the className.
 *
 * @example
 * <LoadingIcon size="sm" />
 * <LoadingIcon size="xs" className="text-text-tertiary" />
 */
export const LoadingIcon = React.forwardRef<
  SVGSVGElement,
  Omit<IconProps, 'name'>
>(({ size, className }, ref) => {
  return (
    <LoaderCircle
      ref={ref}
      className={cn(
        styles({ size, className }),
        '[&>path]:origin-center [&>path]:animate-spin',
      )}
    />
  );
});
